import { ReactComponent as LogoCin7 } from 'assets/images/icons/entityButtonCin7.svg';
import { ReactComponent as LogoExcel } from 'assets/images/icons/entityButtonExcel.svg';
import { ReactComponent as LogoQuickbooks } from 'assets/images/icons/entityButtonQuickbooks.svg';
import { ReactComponent as LogoXero } from 'assets/images/icons/entityButtonXero.svg';
import { ReactComponent as LogoZoho } from 'assets/images/icons/entityButtonZoho.svg';
import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { ConnectCin7 } from 'components/Entity/connect-cin7';
import { EditFriendlyName } from 'components/OrganisationDetails/edit-friendly-name';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import routes from 'routes';
import { Provider } from 'store/api/enums';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import {
  addOrganisationRequest,
  fetchAddUrlRequest,
} from 'store/organisation/actions';
import {
  EditOrganisationParams,
  NewOrganisationParams,
} from 'store/organisation/models';
import { isZohoEnabled } from 'utils/feature-flag-utils';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { PwaIcon } from './Icon';
import { PwaChip, PwaSheet } from './ui';

type PwaAddEntitySheetProps = {
  open: boolean;
  onClose: () => void;
};

export const PwaAddEntitySheet: React.FC<PwaAddEntitySheetProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const loggedUser = useSelector(getAuthUser);
  const dragZoneRef = React.useRef<HTMLDivElement>(null);

  useOverlayBackHandler(open, onClose, 'pwa-add-entity-sheet');
  const dragClose = usePwaBottomSheetDragClose(open, onClose, dragZoneRef);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const connectOrganisation = (providerName: Provider) => {
    if (!loggedUser) return;
    const sourceUrl = location.pathname || routes.main;
    const providerFormattedName = t(`common.${providerName?.toLowerCase()}`, {
      defaultValue: providerName,
    });
    dispatch(
      fetchAddUrlRequest(
        providerName,
        loggedUser.userUuid,
        sourceUrl,
        providerFormattedName
      )
    );
    localStorage.removeItem(`gen-tax-tax-rates-${loggedUser.userUuid}`);
    localStorage.removeItem(`vies-infos-${loggedUser.userUuid}`);
    onClose();
  };

  const createNewEntity = (
    orgValues: EditOrganisationParams,
    providerName: Provider
  ) => {
    const newOrgParams: NewOrganisationParams = {
      providerName,
      legalName: orgValues.entityName || '',
      name: orgValues.newName || '',
      currency: orgValues.currency || '',
      countryCode: orgValues.country || '',
      scope: providerName,
      taxNumber: orgValues.taxNumber || '',
      alternateName: orgValues.alternateName || '',
      code: orgValues.code || '',
      contactPersonName: orgValues.contactPersonName || '',
      contactPersonEmail: orgValues.contactPersonEmail || '',
      contactPersonTelephone: orgValues.contactPersonTelephone || '',
      registeredAddress: orgValues.registeredAddress || '',
      notes: orgValues.notes || '',
    };
    dispatch(addOrganisationRequest(newOrgParams));
    dispatch(turnDrawerOff());
  };

  const onConnectCin7 = () => {
    onClose();
    const drawerData: DrawerData = {
      title: t('organisation.connect-cin7-title'),
      type: DrawerType.CONNECT_CIN7,
      component: <ConnectCin7 />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const onConnectExcel = (providerName: Provider) => {
    onClose();
    const isReceipts = providerName === Provider.RECEIPTS;
    const drawerData: DrawerData = {
      title: t('organisation.add-new-entity'),
      subtitle: isReceipts ? (
        <div style={{ marginTop: 10 }}>
          {t('organisation.connect-entity-receipts-subtitle')}
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {t('organisation.enter-details-below')}
        </div>
      ),
      type: DrawerType.EDIT_FRIENDLY_NAME,
      wide: true,
      component: (
        <EditFriendlyName
          friendlyName=""
          organisationId=""
          onClose={() => dispatch(turnDrawerOff())}
          editOrganisationName={(orgValues) =>
            createNewEntity(orgValues, providerName)
          }
          isAddMode={true}
          addModeProvider={providerName}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  if (!open) return null;

  const zohoEnabled = isZohoEnabled();

  return (
    <PwaSheet
      title={t('organisation.add-new-entity')}
      sub={t('organisation.pwa-add-entity-subtitle')}
      onClose={onClose}
      closeLabel={t('common.close')}
      dragZoneRef={dragZoneRef}
      dragCloseHandlers={dragClose}
    >
      <div className="pwa-section-head" style={{ padding: '4px 4px 0' }}>
        <span>{t('organisation.pwa-add-entity-connect-software')}</span>
      </div>
      <div className="pwa-provgrid">
        <button
          type="button"
          className="pwa-provtile"
          aria-label={t('organisation.connect-with-provider', {
            provider: t('common.xero'),
          })}
          onClick={() => connectOrganisation(Provider.XERO)}
        >
          <span className="pwa-provtile-logo" aria-hidden>
            <LogoXero style={{ width: 40 }} />
          </span>
          <b>{t('common.xero')}</b>
          <span>{t('organisation.pwa-add-entity-note-accounting')}</span>
        </button>
        <button
          type="button"
          className="pwa-provtile"
          aria-label={t('organisation.connect-with-provider', {
            provider: t('common.quickbooks'),
          })}
          onClick={() => connectOrganisation(Provider.QUICKBOOKS)}
        >
          <span className="pwa-provtile-logo" aria-hidden>
            <LogoQuickbooks style={{ width: 40 }} />
          </span>
          <b>{t('common.quickbooks')}</b>
          <span>{t('organisation.pwa-add-entity-note-accounting')}</span>
        </button>
        <button
          type="button"
          className="pwa-provtile"
          aria-label={t('organisation.connect-with-provider', {
            provider: t('common.cin7'),
          })}
          onClick={onConnectCin7}
        >
          <span className="pwa-provtile-logo pwa-provtile-logo--wide" aria-hidden>
            <LogoCin7 style={{ width: 72 }} />
          </span>
          <b>{t('common.cin7')}</b>
          <span>{t('organisation.vat-checker-only')}</span>
        </button>
        <button
          type="button"
          className="pwa-provtile"
          disabled={!zohoEnabled}
          aria-label={
            zohoEnabled
              ? t('organisation.connect-with-provider', {
                  provider: t('common.zoho'),
                })
              : t('organisation.connect-provider-coming-soon', {
                  provider: t('common.zoho'),
                })
          }
          onClick={() => {
            if (zohoEnabled) connectOrganisation(Provider.ZOHO);
          }}
        >
          <span className="pwa-provtile-logo pwa-provtile-logo--wide" aria-hidden>
            <LogoZoho style={{ width: 72 }} />
          </span>
          <b>{t('common.zoho')}</b>
          <span>
            {zohoEnabled
              ? t('organisation.pwa-add-entity-note-accounting')
              : t('group.coming-soon')}
          </span>
        </button>
      </div>

      <div className="pwa-section-head" style={{ padding: '12px 4px 0' }}>
        <span>{t('organisation.pwa-add-entity-without-software')}</span>
      </div>
      <div className="pwa-list">
        <button
          type="button"
          className="pwa-row"
          aria-label={t('organisation.connect-entity-excel-label')}
          onClick={() => onConnectExcel(Provider.EXCEL)}
        >
          <span className="pwa-row-ico" aria-hidden>
            <LogoExcel style={{ width: 22, height: 22 }} />
          </span>
          <span className="pwa-row-body">
            <span className="pwa-row-title">
              {t('organisation.connect-entity-excel-label')}
            </span>
            <span className="pwa-row-sub">
              {t('organisation.pwa-add-entity-excel-sub')}
            </span>
          </span>
          <span className="pwa-row-end" aria-hidden>
            <PwaIcon name="chevron" size={18} />
          </span>
        </button>
        <button
          type="button"
          className="pwa-row"
          aria-label={t('organisation.connect-entity-receipts-label')}
          onClick={() => onConnectExcel(Provider.RECEIPTS)}
        >
          <span className="pwa-row-ico" aria-hidden>
            <AiScanningLogo size={22} />
          </span>
          <span className="pwa-row-body">
            <span className="pwa-row-title">
              {t('organisation.connect-entity-receipts-label')}{' '}
              <PwaChip tone="brand">{t('common.beta')}</PwaChip>
            </span>
            <span className="pwa-row-sub">
              {t('organisation.pwa-add-entity-receipts-sub')}
            </span>
          </span>
          <span className="pwa-row-end" aria-hidden>
            <PwaIcon name="chevron" size={18} />
          </span>
        </button>
      </div>
    </PwaSheet>
  );
};

export default PwaAddEntitySheet;
